/**
 * 🔒 The windowed envelopes a template's preview can be laid over (T191;
 * decision 0662). The product owner: *"a envelope overlay on the top to toggle
 * and have the envelope profiles to make sure they land if using windowed
 * envelopes"*.
 *
 * 🔒 A profile is an `EnvelopeProfile` row, seeded from the figures here. Every
 * window is measured on the sheet the profile names, folded the way it names,
 * with its top panel outward — where the cutout falls on the paper, not on the
 * envelope — so the renderer draws it in the sheet's own inches.
 *
 * 🔨 The number ten double-window profile reads the same two windows the
 * default templates are seeded inside (decision 0660), so the overlay and the
 * seeded places cannot come to disagree.
 */
import type { StringKey } from '../strings';
import { PRINT_PAGE_INCHES, PRINT_PAGE_SIZES, RECIPIENT_ADDRESS_WINDOW, RETURN_ADDRESS_WINDOW, type PrintPageSize, type PrintWindow } from './templates';

/** Which cutout a window is: the sender's, at the top left, or the recipient's, beneath it. */
export const ENVELOPE_WINDOW_ROLES = ['Return', 'Address'] as const;
export type EnvelopeWindowRole = (typeof ENVELOPE_WINDOW_ROLES)[number];

/**
 * 🔒 The sheet a profile's windows are measured on (T207; decision 0698): one of
 * the two sizes a template is laid out on, so a profile names the paper its
 * figures belong to and the editor offers it only over a template on that paper.
 */
export const ENVELOPE_SHEET_SIZES = PRINT_PAGE_SIZES;
export type EnvelopeSheetSize = PrintPageSize;

/**
 * 🔒 How the sheet is folded to go in the envelope (T207; decision 0698). Thirds
 * is a business letter's; thirds then in half is what a Letter sheet takes to
 * reach a number six and three-quarters; quarters is a card's, for the two
 * announcement envelopes; flat is a catalog envelope's, the sheet unfolded.
 */
export const ENVELOPE_FOLDS = ['Thirds', 'ThirdsThenHalf', 'Quarters', 'Flat'] as const;
export type EnvelopeFold = (typeof ENVELOPE_FOLDS)[number];

/** Each fold's name as a person reads it. */
export const ENVELOPE_FOLD_LABEL_KEYS: Readonly<Record<EnvelopeFold, StringKey>> = {
  Thirds: 'print.envelope.foldThirds',
  ThirdsThenHalf: 'print.envelope.foldThirdsThenHalf',
  Quarters: 'print.envelope.foldQuarters',
  Flat: 'print.envelope.foldFlat',
};

/**
 * 🔒 Where a fold creases the sheet, in the sheet's own inches (decision 0698):
 * the lines across and the lines down. The overlay draws them faintly, so a
 * reader sees which panel of the folded sheet faces the windows.
 */
export function envelopeFoldLines(fold: EnvelopeFold, page: { widthInches: number; heightInches: number }): { across: readonly number[]; down: readonly number[] } {
  if (fold === 'Thirds') return { across: [page.heightInches / 3, (page.heightInches * 2) / 3], down: [] };
  if (fold === 'ThirdsThenHalf') return { across: [page.heightInches / 3, (page.heightInches * 2) / 3], down: [page.widthInches / 2] };
  if (fold === 'Quarters') return { across: [page.heightInches / 2], down: [page.widthInches / 2] };
  return { across: [], down: [] };
}

/** One cutout, in inches on the folded sheet. */
export interface EnvelopeWindow {
  role: EnvelopeWindowRole;
  xInches: number;
  yInches: number;
  widthInches: number;
  heightInches: number;
}

/**
 * A profile as the editor lists it and the renderer draws it. 🔒 A profile with
 * no window is allowed (decision 0698): an announcement envelope shows nothing of
 * the sheet, and the overlay says so rather than drawing nothing.
 */
export interface EnvelopeProfileRow {
  id: string;
  name: string;
  sheetSize: EnvelopeSheetSize;
  fold: EnvelopeFold;
  windows: readonly EnvelopeWindow[];
}

/** The list the editor's overlay picker reads. */
export interface EnvelopeProfileListAnswer {
  profiles: readonly EnvelopeProfileRow[];
}

/** One seeded profile: its name, its order in the picker, its sheet and fold, and its cutouts. */
export interface EnvelopeProfileSeed {
  name: string;
  sortOrder: number;
  sheetSize: EnvelopeSheetSize;
  fold: EnvelopeFold;
  address: Omit<EnvelopeWindow, 'role'> | null;
  returnWindow: Omit<EnvelopeWindow, 'role'> | null;
}

function fromWindow(window: Readonly<PrintWindow>): Omit<EnvelopeWindow, 'role'> {
  return { xInches: window.minXInches, yInches: window.minYInches, widthInches: window.maxXInches - window.minXInches, heightInches: window.maxYInches - window.minYInches };
}

/**
 * 🔒 The profiles the seed writes, the number ten double window first (T191, T207;
 * decisions 0662, 0698). The owner, 2026-09-24: *"Add more envelopes and make sure
 * the standards are met."* Every window is where the cutout falls on the folded
 * sheet, with the sheet resting on the envelope's floor and against its left edge.
 *
 * 🔒 The number ten single window is the standard address window — 4 1/2 by
 * 1 1/8 inches, 7/8 of an inch from the envelope's left and 1/2 from its foot —
 * carried onto the sheet with the letter's lower crease resting on the
 * envelope's floor: 3.67 less a half inch less 1 1/8 puts its top 2.04 down.
 *
 * 🔨 Every figure below the two number tens is from memory of the published
 * business-envelope sizes, not read off a specification: the build machine
 * reaches no network. Piece 27 carries them as an open issue with the
 * fold-and-hold test, and decision 0698 names the least certain.
 *
 * - The number nine double window (3 7/8 by 8 7/8): a 3 1/2 by 1 inch window 5/8
 *   from the left and 7/16 from the top, and another 3/4 from the left and 5/8
 *   from the foot. The Letter sheet folded in thirds is 3.67 deep, so it stands
 *   0.21 below the envelope's top and each window sits that much higher on the
 *   sheet than on the envelope: the stored figures (0.23 down for the upper
 *   window) are the envelope's less that 0.21, not the 0.21 itself.
 * - The number six and three-quarters (3 5/8 by 6 1/2): a 3 1/2 by 1 1/8 window
 *   3/4 from the left and 1/2 from the foot. A Letter sheet reaches it folded in
 *   thirds and then in half, 4 1/4 by 3.67, a twentieth deeper than the envelope,
 *   so the sheet's top is taken at the envelope's own.
 * - The two announcement envelopes, A7 (5 1/4 by 7 1/4) and A2 (4 3/8 by 5 3/4),
 *   carry a Letter sheet folded in quarters and no window at all.
 * - The 9 by 12 catalog envelope carries the sheet flat, its 4 1/2 by 1 1/8
 *   window placed over the address block where the number ten's address window has it.
 */
export const ENVELOPE_PROFILE_SEEDS: readonly EnvelopeProfileSeed[] = [
  { name: '#10 double window', sortOrder: 1, sheetSize: 'Letter', fold: 'Thirds', address: fromWindow(RECIPIENT_ADDRESS_WINDOW), returnWindow: fromWindow(RETURN_ADDRESS_WINDOW) },
  { name: '#10 single window', sortOrder: 2, sheetSize: 'Letter', fold: 'Thirds', address: { xInches: 0.875, yInches: 2.04, widthInches: 4.5, heightInches: 1.125 }, returnWindow: null },
  { name: '#9 double window', sortOrder: 3, sheetSize: 'Letter', fold: 'Thirds', address: { xInches: 0.75, yInches: 2.04, widthInches: 3.5, heightInches: 1 }, returnWindow: { xInches: 0.625, yInches: 0.23, widthInches: 3.5, heightInches: 1 } },
  { name: '#6¾ single window', sortOrder: 4, sheetSize: 'Letter', fold: 'ThirdsThenHalf', address: { xInches: 0.75, yInches: 2, widthInches: 3.5, heightInches: 1.125 }, returnWindow: null },
  { name: '9 × 12 catalog window', sortOrder: 5, sheetSize: 'Letter', fold: 'Flat', address: { xInches: 0.875, yInches: 2.25, widthInches: 4.5, heightInches: 1.125 }, returnWindow: null },
  { name: 'A7 announcement', sortOrder: 6, sheetSize: 'Letter', fold: 'Quarters', address: null, returnWindow: null },
  { name: 'A2 announcement', sortOrder: 7, sheetSize: 'Letter', fold: 'Quarters', address: null, returnWindow: null },
];

/** A stored profile's window columns: the address window's four and the return window's four, each empty where the profile has no such window. */
export interface EnvelopeWindowColumns {
  addressXInches: number | null;
  addressYInches: number | null;
  addressWidthInches: number | null;
  addressHeightInches: number | null;
  returnXInches: number | null;
  returnYInches: number | null;
  returnWidthInches: number | null;
  returnHeightInches: number | null;
}

/** A stored profile's columns as its windows, the return window first; a window with any column empty is no window (decision 0698). */
export function envelopeWindowsOf(row: EnvelopeWindowColumns): EnvelopeWindow[] {
  const windows: EnvelopeWindow[] = [];
  if (row.returnXInches !== null && row.returnYInches !== null && row.returnWidthInches !== null && row.returnHeightInches !== null) {
    windows.push({ role: 'Return', xInches: row.returnXInches, yInches: row.returnYInches, widthInches: row.returnWidthInches, heightInches: row.returnHeightInches });
  }
  if (row.addressXInches !== null && row.addressYInches !== null && row.addressWidthInches !== null && row.addressHeightInches !== null) {
    windows.push({ role: 'Address', xInches: row.addressXInches, yInches: row.addressYInches, widthInches: row.addressWidthInches, heightInches: row.addressHeightInches });
  }
  return windows;
}

/** The window columns a seed or a written profile stores, each empty where it has no such window. */
export function envelopeWindowColumns(windows: { address: Omit<EnvelopeWindow, 'role'> | null; returnWindow: Omit<EnvelopeWindow, 'role'> | null }): EnvelopeWindowColumns {
  return {
    addressXInches: windows.address?.xInches ?? null,
    addressYInches: windows.address?.yInches ?? null,
    addressWidthInches: windows.address?.widthInches ?? null,
    addressHeightInches: windows.address?.heightInches ?? null,
    returnXInches: windows.returnWindow?.xInches ?? null,
    returnYInches: windows.returnWindow?.yInches ?? null,
    returnWidthInches: windows.returnWindow?.widthInches ?? null,
    returnHeightInches: windows.returnWindow?.heightInches ?? null,
  };
}

/**
 * 🔒 One profile as Settings › Documents › Envelopes lists it (T208; decision
 * 0699): the profile, its order, whether the editor offers it, and who changed it
 * last, off its audit rows (decision 0604). A hidden profile stays in this list.
 */
export interface EnvelopeProfileSettingsRow extends EnvelopeProfileRow {
  version: number;
  sortOrder: number;
  isActive: boolean;
  /** Who changed the row last and when; null on a row nobody has touched. */
  lastChange: { at: string; byName: string } | null;
}

/** The list the Settings screen reads, hidden profiles included. */
export interface EnvelopeProfileSettingsAnswer {
  rows: readonly EnvelopeProfileSettingsRow[];
}

/** One window as the screen edits it: where it stands on the sheet and how big it is. */
export type EnvelopeWindowBox = Omit<EnvelopeWindow, 'role'>;

/** A profile as the screen writes it: the name, the sheet, the fold and each of the two windows, or none. */
export interface EnvelopeProfileInput {
  name: string;
  sheetSize: EnvelopeSheetSize;
  fold: EnvelopeFold;
  returnWindow: EnvelopeWindowBox | null;
  address: EnvelopeWindowBox | null;
}

/** The longest name the picker shows on one line. */
export const ENVELOPE_PROFILE_NAME_MAX = 60;

/**
 * 🔒 The refusal a profile carries, or null where it holds (T208; decision 0699).
 * The name is not blank and at most sixty characters; the sheet and the fold are
 * members of their lists; and each window the profile has stands on the sheet it
 * names, with a width and a height. 🔨 A profile with no window at all is allowed:
 * an announcement envelope shows nothing of the sheet (decision 0698).
 */
export function envelopeProfileProblem(input: EnvelopeProfileInput): string | null {
  const name = (input.name ?? '').trim();
  if (!name) return 'an envelope needs a name';
  if (name.length > ENVELOPE_PROFILE_NAME_MAX) return `an envelope’s name is at most ${ENVELOPE_PROFILE_NAME_MAX} characters`;
  if (!(ENVELOPE_SHEET_SIZES as readonly string[]).includes(input.sheetSize)) return `an envelope’s sheet is one of ${ENVELOPE_SHEET_SIZES.join(', ')}`;
  if (!(ENVELOPE_FOLDS as readonly string[]).includes(input.fold)) return `an envelope’s fold is one of ${ENVELOPE_FOLDS.join(', ')}`;
  const page = PRINT_PAGE_INCHES[input.sheetSize];
  const windows: readonly (readonly [string, EnvelopeWindowBox | null])[] = [
    ['the return window', input.returnWindow],
    ['the address window', input.address],
  ];
  for (const [label, window] of windows) {
    if (window === null) continue;
    const numbers = [window.xInches, window.yInches, window.widthInches, window.heightInches];
    if (numbers.some((value) => typeof value !== 'number' || !Number.isFinite(value))) return `${label} carries four measurements in inches`;
    if (window.xInches < 0 || window.yInches < 0) return `${label} stands on the sheet, so neither measurement is negative`;
    if (window.widthInches <= 0 || window.heightInches <= 0) return `${label} has a width and a height; untick it instead`;
    if (window.xInches + window.widthInches > page.widthInches + 0.001) return `${label} runs off the right edge of the ${page.widthInches} inch sheet`;
    if (window.yInches + window.heightInches > page.heightInches + 0.001) return `${label} runs off the foot of the ${page.heightInches} inch sheet`;
  }
  return null;
}
